/**
 * Assembles the GST / Ledger / Banking triangulation view-model.
 *
 * Real where cleanly available, mock fallback per field so case 158262 always
 * renders a complete view:
 *   - Bank monthly credits : REAL from statements (per-month records)
 *   - Ledger monthly sales  : REAL from supply-chain financials (best effort)
 *   - GST monthly turnover  : MOCK (no per-month GST turnover API exists)
 *   - ARP                   : REAL avg_repayment_period
 *   - Ledger repayment %    : MOCK (no clean BSA repayment figure)
 *
 * The reconciliation percentages and gating bands are derived from the final
 * (real-overlaid) monthly series, so chart and cards stay consistent.
 */

// Gating bands shared by Ledger Sales and Ledger Repayment checks.
export function bandFromPct(pct) {
    if (pct === null || pct === undefined || Number.isNaN(pct)) {
        return "info";
    }
    if (pct >= 70) {
        return "pass";
    }
    if (pct >= 60) {
        return "warn";
    }
    return "fail";
}

function read(obj, key) {
    if (obj === null || obj === undefined) {
        return undefined;
    }
    if (typeof obj.get === "function") {
        return obj.get(key);
    }
    return obj[key];
}

function toArray(list) {
    if (!list) {
        return [];
    }
    if (typeof list.toArray === "function") {
        return list.toArray();
    }
    return Array.isArray(list) ? list : [];
}

// Parses a rupee value (string/number, possibly comma-formatted) into lakhs.
function toLakhs(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const num = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (Number.isNaN(num)) {
        return null;
    }
    return Math.round((num / 100000) * 10) / 10;
}

const FY_MONTHS = [
    "Apr", "May", "June", "July", "Aug", "Sep",
    "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"
];
const SHORT = {
    Apr: "Apr", May: "May", June: "Jun", July: "Jul", Aug: "Aug", Sep: "Sep",
    Oct: "Oct", Nov: "Nov", Dec: "Dec", Jan: "Jan", Feb: "Feb", Mar: "Mar"
};

// Builds a { "MMM'YY" -> lakhs } map of ledger sales from financials rows.
function ledgerByMonth(financials, displayType) {
    if (!financials || typeof financials.getRows !== "function") {
        return {};
    }
    let rows;
    try {
        rows = toArray(financials.getRows(String(displayType || 1)));
    } catch (err) {
        return {};
    }
    const out = {};
    rows.forEach(row => {
        const yearLabel = String(read(row, "year") || "");
        const startYear = parseInt(yearLabel.slice(0, 4), 10);
        if (Number.isNaN(startYear)) {
            return;
        }
        FY_MONTHS.forEach(month => {
            const lakhs = toLakhs(read(row, month));
            if (lakhs === null) {
                return;
            }
            // Apr–Dec belong to startYear, Jan–Mar to the next calendar year.
            const isEarly = ["Jan", "Feb", "Mar"].includes(month);
            const yy = String((isEarly ? startYear + 1 : startYear) % 100).padStart(2, "0");
            out[`${SHORT[month]}'${yy}`] = lakhs;
        });
    });
    return out;
}

// Builds a { "MMM'YY" -> lakhs } map of bank credits from statement records.
function bankByMonth(statements, bankAccountKey) {
    if (!statements) {
        return {};
    }
    let records;
    try {
        const slice =
            statements.getIn([Number(bankAccountKey), "data"]) ||
            statements.toList().getIn([0, "data"]);
        records = toArray(slice && slice.toList ? slice.toList() : slice);
    } catch (err) {
        return {};
    }
    const out = {};
    records.forEach(rec => {
        const txn = read(rec, "txn_month_year");
        const credit = toLakhs(read(rec, "total_credit_amount"));
        if (txn && typeof txn.format === "function" && credit !== null) {
            out[txn.format("MMM'YY")] = credit;
        }
    });
    return out;
}

const SHORT_MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

// "2025-07" -> "Jul'25"
function monthLabelFromYYYYMM(key) {
    const parts = String(key).split("-");
    if (parts.length < 2) {
        return null;
    }
    const idx = parseInt(parts[1], 10) - 1;
    if (Number.isNaN(idx) || idx < 0 || idx > 11) {
        return null;
    }
    return `${SHORT_MONTHS[idx]}'${String(parts[0]).slice(-2)}`;
}

// Flattens a List/array of { "YYYY-MM": amount } monthly entries (the shape the
// supply-chain financials + repayment APIs return) into { "YYYY-MM": { label,
// lakhs } } keyed by month, so series can be aligned across sources.
function flattenMonthlyData(dataList) {
    const map = {};
    toArray(dataList).forEach(entry => {
        const obj =
            entry && typeof entry.toJS === "function" ? entry.toJS() : entry;
        if (!obj || typeof obj !== "object") {
            return;
        }
        Object.keys(obj).forEach(key => {
            const label = monthLabelFromYYYYMM(key);
            if (!label) {
                return;
            }
            const lakhs = toLakhs(obj[key]);
            if (lakhs === null) {
                return;
            }
            map[key] = { label, lakhs };
        });
    });
    return map;
}

// Builds the REAL monthly triangulation series from supply-chain financials
// (ledger sales), the repayment API and bank statements. Returns null when there
// is no real ledger-sales data to anchor on (caller falls back to the mock).
function buildRealMonthly({ financials, repaymentData, statements, bankAccountKey }) {
    const salesMap = flattenMonthlyData(financials && read(financials, "data"));
    const keys = Object.keys(salesMap).sort();
    if (!keys.length) {
        return null;
    }
    const repayMap = flattenMonthlyData(repaymentData);
    const bankMap = bankByMonth(statements, bankAccountKey);
    const recent = keys.slice(-36); // last 3 years
    return recent.map(key => {
        const label = salesMap[key].label;
        const sales = salesMap[key].lakhs;
        const repay = repayMap[key] ? repayMap[key].lakhs : 0;
        const bank = bankMap[label] !== undefined ? bankMap[label] : 0;
        return {
            month: label,
            // No independent per-month GST feed — mirror the ledger-reconciled
            // sales figure (flagged "indicative" in provenance).
            gst: sales,
            ledger: sales,
            ledgerRepayment: repay,
            bank
        };
    });
}

function sum(series, key) {
    return series.reduce((agg, row) => agg + (Number(row[key]) || 0), 0);
}

function matchPct(a, b) {
    const hi = Math.max(a, b);
    if (!hi) {
        return 0;
    }
    return Math.round((Math.min(a, b) / hi) * 1000) / 10;
}

/**
 * @param {Object} args
 * @param {Object} args.mock          mock.triangulation base (required)
 * @param {*} args.financials         supply-chain financials record
 * @param {*} args.statements         redux statements slice for the case
 * @param {number} args.bankAccountKey active/key bank account id
 * @param {*} args.supplyChainRecord  supply-chain record (ARP, repayments)
 * @param {number|string} args.displayType sales_analysis_display_type
 * @returns {Object|null} triangulation view-model
 */
export function buildTriangulation({
    mock,
    financials,
    repaymentData,
    statements,
    bankAccountKey,
    supplyChainRecord,
    displayType
}) {
    if (!mock || !mock.monthly) {
        return null;
    }
    // Prefer the REAL series — ledger sales (supply-chain financials), repayment
    // (repayment API) and bank credits (statements). GST per-month has no API,
    // so it mirrors the ledger-reconciled sales (flagged indicative). Fall back
    // to the coherent mock baseline when there is no real ledger-sales data.
    const realMonthly = buildRealMonthly({
        financials,
        repaymentData,
        statements,
        bankAccountKey
    });
    const usingReal = Boolean(realMonthly && realMonthly.length);
    const ledgerReal = usingReal;
    const bankReal = usingReal;
    const monthly = usingReal
        ? realMonthly
        : mock.monthly.map(entry => ({ ...entry }));

    const gstTotal = sum(monthly, "gst");
    const ledgerTotal = sum(monthly, "ledger");
    const ledgerRepaymentTotal = sum(monthly, "ledgerRepayment");
    const bankTotal = sum(monthly, "bank");
    const captions = mock.captions || {};

    // All three reconciliations are DERIVED from the same monthly series so the
    // numbers stay internally consistent (no free-floating constants):
    //   Sales         = GST turnover     ↔ ledger sales
    //   Repayment     = bank credits     ↔ ledger repayments (BSA)
    //   Cash coverage = bank credits     ↔ GST turnover
    const salesPct = matchPct(gstTotal, ledgerTotal);
    const repaymentPct = ledgerRepaymentTotal
        ? matchPct(bankTotal, ledgerRepaymentTotal)
        : mock.repaymentMatchPct;
    const coveragePct = matchPct(gstTotal, bankTotal);

    const reconciliations = [
        {
            key: "gst_ledger",
            label: "GST ↔ Ledger",
            pct: salesPct,
            caption: captions.gst_ledger
        },
        {
            key: "bank_ledger",
            label: "Bank ↔ Ledger",
            pct: repaymentPct,
            caption: captions.ledger_bank
        },
        {
            key: "gst_bank",
            label: "GST ↔ Bank",
            pct: coveragePct,
            caption: captions.gst_bank
        }
    ].map(rec => ({ ...rec, band: bandFromPct(rec.pct) }));

    // ARP — real avg_repayment_period when present.
    const arpDays = read(supplyChainRecord, "avg_repayment_period");
    const arp =
        arpDays !== undefined && arpDays !== null && arpDays !== ""
            ? { value: `${Math.round(Number(arpDays))} days`, real: true }
            : { value: mock.arpFallback && mock.arpFallback.value, real: false };

    const checks = [
        {
            key: "ledger_sales",
            label: "Ledger Sales",
            sub: "GST turnover ↔ Tally ledger",
            value: `${salesPct}%`,
            band: bandFromPct(salesPct),
            note:
                salesPct >= 70
                    ? "Match ≥ 70% — pass"
                    : salesPct >= 60
                        ? "60–70% — verify on call"
                        : "< 60% — hard gate"
        },
        {
            key: "ledger_repayment",
            label: "Ledger Repayment",
            sub: "Ledger repayments ↔ BSA entries",
            value: `${repaymentPct}%`,
            band: bandFromPct(repaymentPct),
            note:
                repaymentPct >= 70
                    ? "Match ≥ 70% — pass"
                    : repaymentPct >= 60
                        ? "60–70% — verify on call"
                        : "< 60% — hard gate"
        },
        {
            key: "arp",
            label: "ARP",
            sub: "Average repayment period",
            value: arp.value || "—",
            band: "info",
            note: arp.real ? "From banking analysis" : "Indicative"
        }
    ];

    // Correlation edges for the triangulation graph. Per the analyst model:
    // GST↔Ledger reconciles SALES; BSA(Bank)↔Ledger reconciles REPAYMENT;
    // GST↔Bank is the residual cash-coverage signal.
    const correlations = {
        sales: { pct: salesPct, band: bandFromPct(salesPct) },
        repayment: { pct: repaymentPct, band: bandFromPct(repaymentPct) },
        coverage: { pct: coveragePct, band: bandFromPct(coveragePct) }
    };

    return {
        monthly,
        reconciliations,
        correlations,
        checks,
        flags: mock.flags || [],
        provenance: {
            gst: "mock",
            ledger: ledgerReal ? "real" : "mock",
            bank: bankReal ? "real" : "mock",
            arp: arp.real ? "real" : "mock",
            repayment: usingReal ? "real" : "mock"
        }
    };
}

// "Apr 2024" (API month label) → "Apr'24" (the triangulation grid label).
function shortMonthLabel(raw) {
    const parts = String(raw || "").trim().split(/\s+/);
    if (parts.length < 2) {
        return raw;
    }
    return `${parts[0]}'${String(parts[1]).slice(-2)}`;
}

// Reads the case-data-comparison API response (the single backend source for
// GST × Banking × Ledger) and returns the same view-model shape buildTriangulation
// emits — so the TriangulationSection component is unaware of where the data
// came from. Reconciliation %s come straight from the API (which already
// aligns months and ignores empties), so the chart and the cards stay
// internally consistent.
export function buildTriangulationFromApi(apiResponse, { supplyChainRecord } = {}) {
    if (!apiResponse) {
        return null;
    }
    const tri = apiResponse.triangulation || {};
    const rows = apiResponse.common_months_comparison || [];
    if (!rows.length) {
        return null;
    }
    const monthly = rows.map(row => ({
        month: shortMonthLabel(row.month),
        gst: toLakhs(row.gst_sales) || 0,
        gstPurchase: toLakhs(row.gst_purchase) || 0,
        ledger: toLakhs(row.ledger_sales) || 0,
        ledgerRepayment: toLakhs(row.ledger_repayment) || 0,
        bank: toLakhs(row.bank_credit) || 0,
        bankDebit: toLakhs(row.bank_debit) || 0,
        // GST filing delay in days for the month (null when not filed / no data).
        filingDelay:
            row.filing_delay_in_days === null ||
            row.filing_delay_in_days === undefined
                ? null
                : Number(row.filing_delay_in_days)
    }));

    const salesPct = tri.gst_ledger_overall_match_pct;
    const repaymentPct = tri.bank_ledger_repayment_pct;
    const coveragePct = tri.gst_bank_cash_coverage_pct;

    const reconciliations = [
        {
            key: "gst_ledger",
            label: "GST ↔ Ledger",
            pct: salesPct,
            caption: "Sales reconciliation"
        },
        {
            key: "bank_ledger",
            label: "Bank ↔ Ledger",
            pct: repaymentPct,
            caption: "Repayment reconciliation"
        },
        {
            key: "gst_bank",
            label: "GST ↔ Bank",
            pct: coveragePct,
            caption: "Cash-coverage residual"
        }
    ].map(rec => ({ ...rec, band: bandFromPct(rec.pct) }));

    const correlations = {
        sales: { pct: salesPct, band: bandFromPct(salesPct) },
        repayment: { pct: repaymentPct, band: bandFromPct(repaymentPct) },
        coverage: { pct: coveragePct, band: bandFromPct(coveragePct) }
    };

    // ARP — same as the mock-aware builder: real avg_repayment_period when
    // present on the supply-chain record, else surfaced as "—".
    const arpDays = read(supplyChainRecord, "avg_repayment_period");
    const arpReal =
        arpDays !== undefined && arpDays !== null && arpDays !== "";
    const arpValue = arpReal ? `${Math.round(Number(arpDays))} days` : "—";

    function noteForPct(pct) {
        if (pct === null || pct === undefined || Number.isNaN(pct)) {
            return "Insufficient overlapping months";
        }
        if (pct >= 70) {
            return "Match ≥ 70% — pass";
        }
        if (pct >= 60) {
            return "60–70% — verify on call";
        }
        return "< 60% — hard gate";
    }

    const checks = [
        {
            key: "ledger_sales",
            label: "Ledger Sales",
            sub: "GST turnover ↔ Tally ledger",
            value: salesPct === null || salesPct === undefined ? "—" : `${salesPct}%`,
            band: bandFromPct(salesPct),
            note: noteForPct(salesPct)
        },
        {
            key: "ledger_repayment",
            label: "Ledger Repayment",
            sub: "Ledger repayments ↔ BSA entries",
            value:
                repaymentPct === null || repaymentPct === undefined
                    ? "—"
                    : `${repaymentPct}%`,
            band: bandFromPct(repaymentPct),
            note: noteForPct(repaymentPct)
        },
        {
            key: "arp",
            label: "ARP",
            sub: "Average repayment period",
            value: arpValue,
            band: "info",
            note: arpReal ? "From banking analysis" : "Not available"
        }
    ];

    return {
        monthly,
        reconciliations,
        correlations,
        checks,
        flags: [],
        provenance: {
            gst: "real",
            ledger: "real",
            bank: "real",
            arp: arpReal ? "real" : "mock",
            repayment: "real"
        }
    };
}

export default buildTriangulation;
